import { useCallback } from "react";
import { useFetcher, useMutator } from "~/hooks/useFetcher";
import {
  notificationPreferencesPath,
  type NotificationDelivery,
  type NotificationPreferences,
} from "~/services/notifications";

export interface NotificationDeliverySetting {
  /** The stored choice, or `null` until it has loaded. */
  delivery: NotificationDelivery | null;
  error: Error | undefined;
  isSaving: boolean;
  /** Rejects reach the caller; the stored choice is unchanged then. */
  save: (next: NotificationDelivery) => Promise<void>;
}

/**
 * Reads and writes how the user wants notifications delivered, on the
 * conversations service's notification preferences endpoint. The choice moves
 * optimistically and rolls back if the write fails.
 */
export const useNotificationDelivery = (): NotificationDeliverySetting => {
  const { data, error } = useFetcher<NotificationPreferences>(notificationPreferencesPath);
  const { trigger, isMutating } = useMutator<NotificationPreferences, NotificationPreferences>(
    notificationPreferencesPath,
    { requestInit: { method: "PUT" } }
  );

  const save = useCallback(
    async (next: NotificationDelivery) => {
      await trigger(
        { delivery: next },
        { optimisticData: { delivery: next }, rollbackOnError: true }
      );
    },
    [trigger]
  );

  return { delivery: data?.delivery ?? null, error, isSaving: isMutating, save };
};
